import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { PencilSimpleIcon } from '@phosphor-icons/react/dist/csr/PencilSimple';
import { SwapIcon } from '@phosphor-icons/react/dist/csr/Swap';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';

import { DeviceFormDialog } from './DeviceFormDialog';
import { PageActions } from '../../app/shell/PageActions';
import { FilterBar } from '../../design-system/composites/FilterBar/FilterBar';
import { deviceModelLabel } from './deviceFormHelpers';
import { DeviceStatusDialog } from './DeviceStatusDialog';
import { Button } from '../../design-system/primitives/Button/Button';
import {
  DataTable,
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { Pagination } from '../../design-system/composites/Pagination/Pagination';
import { useCursorPages } from '../../design-system/composites/Pagination/useCursorPages';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import { ApiError } from '../../lib/api/client';
import {
  deviceModelsApi,
  sensorsApi,
  type DeviceModel,
  type Sensor,
  type SensorQuery,
  type SensorStatus,
} from '../../lib/api/devices';
import styles from './DeviceList.module.css';

/**
 * The sensor registry — filter bar, table, cursor pagination. Follows
 * `AuditLogScreen.tsx` for the paginator (draft vs. applied filters, an
 * opaque cursor read out of `meta.next_cursor` and handed back verbatim,
 * never parsed) and `DispensaryList.tsx` for the create/edit wiring.
 *
 * `expiring_before` IS SENSOR-ONLY — see `devices.ts`'s own docblock.
 * `ReaderList.tsx` deliberately carries no equivalent field; the two files
 * are not merged into one generic component precisely so this asymmetry
 * cannot be smoothed away by a shared prop neither kind quite fits.
 */

export interface SensorListProps {
  permissions: readonly string[];
  onOpen: (uuid: string) => void;
  /** False while a device detail covers the list (the list stays mounted,
   *  hidden — `DeviceScreen`'s docblock): its Add command is portalled into
   *  the shell's title row and must leave with it. Defaults to true. */
  active?: boolean;
}

interface SensorRow extends Row, Sensor {}

function toRow(node: Sensor): SensorRow {
  return { ...node, id: node.uuid };
}

const SENSOR_STATUSES: readonly SensorStatus[] = [
  'in_stock',
  'assigned',
  'dispensed',
  'active',
  'expired',
  'faulty',
  'replaced',
  'returned',
];

interface FilterState {
  status: string;
  type: string;
  dispensary: string;
  serial: string;
  expiringBefore: string;
}

const BLANK_FILTERS: FilterState = {
  status: '',
  type: '',
  dispensary: '',
  serial: '',
  expiringBefore: '',
};

function toQuery(filters: FilterState, cursor?: string, perPage?: number): SensorQuery {
  const query: SensorQuery = {};
  if (filters.status !== '') query.status = filters.status as SensorStatus;
  if (filters.type !== '') query.type = filters.type;
  if (filters.dispensary.trim() !== '') query.dispensary = filters.dispensary.trim();
  if (filters.serial.trim() !== '') query.serial = filters.serial.trim();
  if (filters.expiringBefore !== '') query.expiring_before = filters.expiringBefore;
  if (cursor !== undefined) query.cursor = cursor;
  if (perPage !== undefined) query.per_page = perPage;
  return query;
}

function hasAppliedFilters(query: SensorQuery): boolean {
  return (
    query.status !== undefined ||
    query.type !== undefined ||
    query.dispensary !== undefined ||
    query.serial !== undefined ||
    query.expiring_before !== undefined
  );
}

export function SensorList({ permissions, onOpen, active = true }: SensorListProps) {
  const { t, i18n } = useTranslation();
  const canManage = permissions.includes('device.manage');

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly SensorRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  // 50 — `SensorIndexRequest::DEFAULT_PER_PAGE` server-side.
  const pages = useCursorPages();
  // Destructured — `pages` is a fresh object every render; see
  // `CaseList.tsx`'s identical note.
  const { observe } = pages;
  const [draftFilters, setDraftFilters] = useState<FilterState>(BLANK_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(BLANK_FILTERS);
  const [models, setModels] = useState<readonly DeviceModel[]>([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editingNode, setEditingNode] = useState<Sensor | undefined>(undefined);
  const [statusNode, setStatusNode] = useState<Sensor | null>(null);

  const requestSeq = useRef(0);

  const load = useCallback(async (query: SensorQuery) => {
    const seq = ++requestSeq.current;
    setStatus('loading');
    setReference(null);

    try {
      const page = await sensorsApi.list(query);
      if (seq !== requestSeq.current) return;
      setRows(page.data.map(toRow));
      // `DevicePage.meta` carries no `total` — see the `<Pagination
      // total={null}>` calls below.
      observe({ next_cursor: page.meta.next_cursor, total: null });
      setStatus(page.data.length === 0 ? (hasAppliedFilters(query) ? 'no-results' : 'empty') : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (seq !== requestSeq.current) return;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, [observe]);

  useEffect(() => {
    void load(toQuery(appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }, [load, appliedFilters, pages.cursor, pages.perPage]);

  useEffect(() => {
    void deviceModelsApi.list().then((all) => {
      setModels(all.filter((model) => model.kind === 'sensor'));
    });
  }, []);

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    pages.first();
    setAppliedFilters(draftFilters);
  }

  function handleClearFilters(): void {
    setDraftFilters(BLANK_FILTERS);
    pages.first();
    setAppliedFilters(BLANK_FILTERS);
  }

  function retry(): void {
    void load(toQuery(appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }

  function updateDraft(field: keyof FilterState) {
    return (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value } = event.target;
      setDraftFilters((current) => ({ ...current, [field]: value }));
    };
  }

  function typeLabel(node: Sensor): string {
    const label = i18n.language.startsWith('ar') ? node.type_label_ar : node.type_label_en;
    return label ?? node.type;
  }

  function applyRowChange(uuid: string, changes: Partial<Sensor>): void {
    setRows((current) => current.map((row) => (row.id === uuid ? { ...row, ...changes } : row)));
  }

  function openCreateForm(): void {
    setEditingNode(undefined);
    setFormOpen(true);
  }

  function openEditForm(node: Sensor): void {
    setEditingNode(node);
    setFormOpen(true);
  }

  function handleSaved(saved: Sensor): void {
    setRows((current) => {
      const exists = current.some((row) => row.id === saved.uuid);
      return exists
        ? current.map((row) => (row.id === saved.uuid ? { ...row, ...saved } : row))
        : [...current, toRow(saved)];
    });
    if (status === 'empty') setStatus('ready');
    setFormOpen(false);
  }

  function renderType(row: Row): ReactNode {
    return typeLabel(row as unknown as Sensor);
  }

  function renderStatus(row: Row): ReactNode {
    return t(`devices.status.${(row as unknown as Sensor).status}`);
  }

  function renderDispensary(row: Row): ReactNode {
    return (row as unknown as Sensor).dispensary?.name ?? null;
  }

  function renderActions(row: Row): ReactNode {
    const node = row as unknown as Sensor;
    return (
      <div className={styles['rowActions']}>
        <Button
          icon={<PencilSimpleIcon size={16} />}
          variant="ghost"
          onClick={() => {
            openEditForm(node);
          }}
        >
          {t('devices.edit')}
        </Button>
        <Button
          icon={<SwapIcon size={16} />}
          variant="ghost"
          onClick={() => {
            setStatusNode(node);
          }}
        >
          {t('devices.changeStatus')}
        </Button>
      </div>
    );
  }

  const statusOptions: readonly SelectOption[] = SENSOR_STATUSES.map((value) => ({
    value,
    label: t(`devices.status.${value}`),
  }));
  const typeOptions: readonly SelectOption[] = models.map((model) => ({
    value: model.code,
    label: deviceModelLabel(model, i18n.language),
  }));

  const columns: readonly Column[] = [
    { key: 'serial_number', label: t('devices.columnSerial'), neverMirrors: true },
    { key: 'type', label: t('devices.columnType'), render: renderType },
    { key: 'status', label: t('devices.columnStatus'), render: renderStatus },
    { key: 'dispensary', label: t('devices.columnDispensary'), render: renderDispensary },
    ...(canManage
      ? [{ key: 'actions', label: t('devices.columnActions'), render: renderActions }]
      : []),
  ];

  return (
    <div className={styles['page']}>
      {canManage && active ? (
        <PageActions>
          <Button onClick={openCreateForm} icon={<PlusIcon size={16} />}>
            {t('devices.addSensor')}
          </Button>
        </PageActions>
      ) : null}

      <FilterBar onSubmit={handleFilterSubmit} onClear={handleClearFilters}>
        <Select
          label={t('devices.filterStatus')}
          width="compact"
          placeholder={t('devices.anyStatus')}
          options={statusOptions}
          value={draftFilters.status}
          onChange={updateDraft('status')}
        />
        <Select
          label={t('devices.filterType')}
          width="compact"
          placeholder={t('devices.anyType')}
          options={typeOptions}
          value={draftFilters.type}
          onChange={updateDraft('type')}
        />
        <Input
          label={t('devices.filterDispensary')}
          width="compact"
          dir="ltr"
          value={draftFilters.dispensary}
          onChange={updateDraft('dispensary')}
        />
        <Input
          label={t('devices.filterSerial')}
          width="compact"
          dir="ltr"
          value={draftFilters.serial}
          onChange={updateDraft('serial')}
        />
        <Input
          label={t('devices.filterExpiringBefore')}
          width="compact"
          type="date"
          value={draftFilters.expiringBefore}
          onChange={updateDraft('expiringBefore')}
        />

      </FilterBar>

      <DataTable
        caption={t('devices.tabSensors')}
        columns={columns}
        rows={rows}
        status={status}
        empty={{ body: t('devices.emptySensors') }}
        noResults={{ title: t('devices.noResultsSensors') }}
        onClearFilters={handleClearFilters}
        error={{ title: t('devices.loadFailedSensors') }}
        onRowActivate={(row) => {
          onOpen(row.id);
        }}
      />

      <Pagination pages={pages} total={null} rowsOnPage={rows.length} />

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button icon={<ArrowClockwiseIcon size={16} />} variant="secondary" onClick={retry}>
            {t('devices.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="sensorListRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      <DeviceFormDialog
        kind="sensor"
        open={formOpen}
        node={editingNode}
        onClose={() => {
          setFormOpen(false);
        }}
        onSaved={(saved) => {
          handleSaved(saved as Sensor);
        }}
      />

      {statusNode === null ? null : (
        <DeviceStatusDialog
          kind="sensor"
          open={statusNode !== null}
          device={statusNode}
          onClose={() => {
            setStatusNode(null);
          }}
          onChanged={(updated) => {
            applyRowChange(updated.uuid, updated as Partial<Sensor>);
            setStatusNode(null);
          }}
        />
      )}
    </div>
  );
}
